import { defineComponent as _defineComponent } from 'vue'
import DropDown from "@/components/ui/dropdown/DropDown.vue";
  import SwitchComponent from "@/components/ui/SwitchComponent.vue";
  import EditLotModal from "@/components/ui/modals/EditLotModal.vue";
  import LotLifecycle from "@/components/LotLifecycle.vue";
  import CreateOptionModal from "@/components/ui/modals/CreateOptionModal.vue";
  import LotDetailsConfirmModal from "@/components/ui/modals/LotDetailsConfirmModal.vue";
  import ReleaseLotsModal from "@/components/ui/modals/ReleaseLotsModal.vue";
  import TransitionModal from "@/components/ui/modals/TransitionModal.vue";
  import LotAssignmentModal from "@/components/ui/modals/LotAssignmentModal.vue";
  import SendContractFormModal from "@/components/ui/modals/SendContractFormModal.vue";
  import CreateHoldFromLandownerSideModal from "@/components/ui/modals/CreateHoldFromLandownerSideModal.vue";
  import { computed, onMounted, ref, watch, onBeforeUnmount } from "vue";
  import router from "@/router";
  import store from "@/store";
  import { getLotPreviousStatus, getLotStatusClass } from "@/helpers/status";
  import { formatDate } from "@/helpers/date";
  import { useLotHook } from "@/hooks/lot";
  import { StageInterface } from "@/types/StageInterface";
  import { LotStatus } from "@/enums/LotStatus";
  import { LotDocument, LotInterface } from "@/types/LotInterface";
  import { EoiStatus } from "@/enums/EoiStatus";
  import { EoiGroupByLotInterface } from "@/types/EoiInterface";
  import { useToast } from "vue-toastification";
  import { LotOfflineUpdateInterface } from "@/types/LotOfflineUpdateInterface";
  import { OfflinePhases } from "@/enums/OfflinePhase";
  import { useRoute } from "vue-router";
  import { InitialHoldWithLotInterface } from "@/types/InitialHoldInterface";
  import GiveReasonModal from "@/components/ui/modals/GiveReasonModal.vue";
  import { ReasonsForCancelEOI } from "@/enums/ReasonsForCancelEOI";
  import TransferOwnershipModal from "@/components/ui/modals/TransferOwnershipModal.vue";
  import { EstateInterface } from "@/types/EstateInterface";
  import { getUser } from "@/features/useUser";
  import { isLotOwner } from "@/helpers/lot/isLotOwner";
  import { AxiosResponse } from "axios";
  import { Business } from "@/types/BusinessInterface";
  import { getBusinessNameFromLot } from "@/helpers/lot/getBusinessNameFromLot";
  import { getBusinessColorFromLot } from "@/helpers/lot/getBusinessColorFromLot";
  
export default /*@__PURE__*/_defineComponent({
  __name: 'Lot',
  setup(__props, { expose: __expose }) {
  __expose();

  const route = useRoute();
  const {
    showTransitionModal,
    showReleaseModal,
    attentionType,
    showSendContractFormModal,
    toProfile,
  } = useLotHook();
  const toast = useToast();
  const estateId = computed(() => store.state.user.selectedEstateId);
  const stageId = router.currentRoute.value.params.stageId;
  const lotId = router.currentRoute.value.params.lotId;
  const stage = ref<StageInterface | null>(null);
  const lot = ref<LotInterface | null>(null);
  const eois = ref<EoiGroupByLotInterface | null>(null);
  const businesses = ref<Business[]>([]);
  const showCreateInitialHoldModal = ref(false);
  const userBusiness = ref<Business | null>(null);
  const initialHoldsCount = ref<number>(0);
  const showCancelContractModal = ref(false);
  const showTransferOwnershipModal = ref(false);
  const estate = ref<EstateInterface | null>(null);
  const hasCollaborator = computed(() => {
    return (
      estate.value?.collaborators?.length &&
      estate.value?.collaboratorBusiness?.length
    );
  });
  const onCloseTransferOwnershipModal = () => {
    getLot();
  };

  const canManageLot = computed(() =>
    isLotOwner(lot.value as LotInterface, userBusiness.value, estate.value)
  );
  const getEstate = () => {
    store.dispatch("estate/getEstate", estateId.value).then((res) => {
      estate.value = res.data;
    });
  };
  const canTransferLot = computed(() => {
    return (
      hasCollaborator.value && canManageLot.value && !!lot.value?.ownershipId
    );
  });
  const transferOwnership = async () => {
    if (
      !lot.value ||
      !estate.value ||
      !canManageLot.value ||
      !hasCollaborator.value
    )
      return;

    if (!estate.value?.collaboratorBusiness) return;

    if (!lot.value?.ownershipId) return;

    const currentOwnershipId =
      typeof lot.value.ownershipId === "object"
        ? lot.value.ownershipId._id
        : lot.value.ownershipId;

    const estateBusinessId = estate.value.business?._id;
    const collaboratorBusinessId = estate.value.collaboratorBusiness[0]._id;

    const newOwnershipId =
      currentOwnershipId === estateBusinessId
        ? collaboratorBusinessId
        : estateBusinessId;

    loading.value = true;

    try {
      await store.dispatch("lot/transferOwnership", {
        lotId: lot.value?._id,
        estateId: estateId.value,
        ownershipId: newOwnershipId,
      });
      getLot();
      toast.success("Ownership transferred successfully");
    } catch (error) {
      toast.error("Something went wrong");
    } finally {
      loading.value = false;
    }
  };

  const tabs = computed(() => {
    const tabList = [
      { label: "actions" },
      { label: "documents" },
      { label: "contracts" },
      { label: "home & land" },
      { label: "hold requests", count: initialHoldsCount.value },
      { label: "contract information forms", count: eois.value?.eois?.length },
      { label: "notes" },
    ];

    if (lot.value?.eoiId) {
      tabList.push({ label: "conditions" });
    }

    if (lot.value?.status === LotStatus.OPTION) {
      tabList.push({ label: "options" });
    }

    if (
      lot.value?.offlineSale &&
      (lot.value?.status === LotStatus.OFFLINE ||
        lot.value?.status === LotStatus.SETTLED)
    ) {
      tabList.splice(1, 1);
    }

    return tabList;
  });

  const getLotStatus = computed(() => {
    if (lot.value && lot.value.status) {
      if (lot.value.status === LotStatus.EOI) {
        return LotStatus.PRE_CONTRACT;
      }

      return lot.value.status;
    }

    return "";
  });

  const currentTab = ref(getCurrentTab());
  const showEdit = ref(false);
  const showCreateOptionModal = ref(false);
  const createOptionObject = computed(() => {
    return [
      {
        id: lot.value?._id,
        selected: true,
        status: lot.value?.status,
      },
    ];
  });
  const showConfirmActionModal = ref("");
  const showConfirmMakeOffline = ref(false);
  const OfflinePhase = OfflinePhases;
  const loading = ref(true);
  const onShowCancelContractModal = () => {
    showCancelContractModal.value = true;
    loading.value = false;
  };
  const onCancelContract = (reason: string) => {
    if (!lot.value) return;

    loading.value = true;
    showCancelContractModal.value = false;
    store
      .dispatch("eoi/revokeEoi", {
        eoiId: lot.value?.eoiId?._id,
        lotId: lot.value?._id,
        reasonForTermination: reason,
      })
      .then(() => {
        getLot();
      });
  };

  onMounted(() => {
    store.dispatch("stage/getStage", stageId).then((res) => {
      stage.value = res.data;
    });
    getEstate();
    getLot().then(() => {
      if (!currentTab.value) {
        currentTab.value = getCurrentTab();
      }
    });
    getEois();
    setInitialHoldsCount();
    getBusinesses();
    getUserBusiness();
  });

  onBeforeUnmount(() => {
    toast.clear();
  });

  watch(
    () => estateId.value,
    () => {
      router.push({ name: "Stages" });
    }
  );

  const makeOffline = () => {
    showConfirmMakeOffline.value = false;
    store
      .dispatch("lot/changeLotStatus", {
        id: lot.value._id,
        data: {
          status: LotStatus.OFFLINE,
          offlineSale: {
            offlineDeposit: 0,
            salePhase: "EOI",
          },
        },
      })
      .then(() => {
        getLot().then(() => {
          changeTab("notes");
        });
      });
  };
  const onCreatedHold = () => {
    loading.value = true;
    getLot().finally(() => {
      loading.value = false;
    });
  };

  function changeOfflinePhase(value: OfflinePhases) {
    const data = {
      id: lot.value._id,
      data: {
        offlineSale: {
          salePhase: value,
        },
      } as LotOfflineUpdateInterface,
    };

    store.dispatch("lot/updateOffline", data).then(() => {
      getLot();
    });
  }
  function toTab(tab: string) {
    if (currentTab.value.label !== tab) {
      router.push(
        router.currentRoute.value.fullPath.replace(currentTab.value.label, tab)
      );
    }
  }
  function changeTab(tab: string) {
    router.push(
      router.currentRoute.value.fullPath.replace(currentTab.value.label, tab)
    );
    currentTab.value = tabs.value.find((item) => item.label === tab);
  }
  const getLot = () => {
    return store
      .dispatch("lot/getLot", lotId)
      .then((res) => {
        lot.value = res.data;

        if (lot.value?.status === LotStatus.OFFLINE) {
          toast(
            "This lot is currently being managed in offline-mode. Purchasers cannot submit new hold requests on offline lots.",
            {
              closeOnClick: true,
              timeout: false,
              toastClassName: "offline-lot-toast",
              bodyClassName: ["offline-lot-toast"],
            }
          );
        }
      })
      .finally(() => (loading.value = false));
  };

  function getEois() {
    store
      .dispatch("eoi/getEoisForLot", {
        id: lotId,
        status: [EoiStatus.PENDING],
        estateId: estateId.value,
      })
      .then((res) => {
        eois.value = res.data;
      });
  }

  const getBusinesses = () => {
    store
      .dispatch("business/getAllBusiness")
      .then((res: AxiosResponse<Business[]>) => {
        businesses.value = res.data;
      });
  };

  const getUserBusiness = () => {
    loading.value = true;

    store
      .dispatch("business/getBusinessByLandownerId")
      .then((res: AxiosResponse<Business>) => {
        userBusiness.value = res.data;
      })
      .finally(() => {
        loading.value = false;
      });
  };

  const getEoisWithWaitingForApprove = () => {
    return store
      .dispatch("eoi/getEoisForLot", {
        id: lotId,
        status: [EoiStatus.WAITING_FOR_APPROVED],
        estateId: estateId.value,
      })
      .then((res) => {
        return res;
      });
  };

  const setInitialHoldsCount = async () => {
    try {
      const { data } = await store.dispatch(
        "initialHold/getActiveInitialHolds",
        estateId.value
      );
      const initialHoldWithLot = data?.find(
        (initialHold: InitialHoldWithLotInterface) => initialHold._id === lotId
      );

      initialHoldsCount.value = initialHoldWithLot?.initialHold.length ?? 0;
    } catch (error) {
      toast.error("Something went wrong, Please try again later.");
    }
  };
  const checkForUnrelease = () => {
    getEoisWithWaitingForApprove().then((response) => {
      if (response.data.eois) {
        const isSomeIsWithWaitingForApprove = response.data.eois.some((eoi) => {
          return eoi.status === EoiStatus.WAITING_FOR_APPROVED;
        });

        if (isSomeIsWithWaitingForApprove) {
          showTransitionModal.value = true;
          attentionType.value = "UnreleaseWaitingForApprove";
        }
      }

      if (eois.value?.eois?.length) {
        attentionType.value = "unrelease";
        showTransitionModal.value = true;
      } else {
        if (attentionType.value !== "UnreleaseWaitingForApprove") {
          showConfirmActionModal.value = "unrelease";
        }
      }
    });
  };
  const checkForCreateOption = () => {
    getEoisWithWaitingForApprove().then((response) => {
      if (response.data.eois) {
        const isSomeIsWithWaitingForApprove = response.data.eois.some((eoi) => {
          return eoi.status === EoiStatus.WAITING_FOR_APPROVED;
        });

        if (isSomeIsWithWaitingForApprove) {
          showTransitionModal.value = true;
          attentionType.value = "OptionWaitingForApprove";
        }
      }

      if (eois.value?.eois?.length) {
        attentionType.value = "createOptions";
        showTransitionModal.value = true;
      } else {
        if (attentionType.value !== "OptionWaitingForApprove") {
          showCreateOptionModal.value = true;
        }
      }
    });
  };
  const checkProceedModal = () => {
    if (
      [
        "unrelease",
        "createOptions",
        "UnreleaseWaitingForApprove",
        "OptionWaitingForApprove",
      ].includes(attentionType.value)
    ) {
      showTransitionModal.value = false;
      changeTab("Contract Information Forms");
    } else {
      toProfile();
    }
  };

  function closeCreateOptionModal(update: boolean) {
    if (update) {
      getLot();
    }

    showCreateOptionModal.value = false;
  }
  function getCurrentTab() {
    return tabs.value.find((label) =>
      router.currentRoute.value.fullPath.includes(label.label)
    );
  }
  function generateContract() {
    loading.value = true;
    store
      .dispatch("eoi/downloadPdf", lot.value?.eoiId?._id)
      .then((res) => {
        if (!lot.value?.eoiId?.pdfLink) {
          getLot();
        } else {
          loading.value = false;
        }

        window.open(res.data, "_blank");
      })
      .catch(() => toast.error("Something went wrong"));
  }
  function closeReleaseModal(confirm: boolean) {
    if (confirm) {
      getLot();
    }

    showReleaseModal.value = false;
  }
  function addDocument(document: {
    file: LotDocument;
    documentType: "contracts" | "documents";
  }) {
    lot.value[document.documentType].unshift(document.file);
  }
  function deleteDocument(document: {
    index: number;
    documentType: "contracts" | "documents";
  }) {
    lot.value[document.documentType].splice(document.index, 1);
  }
  const toStage = () => {
    router.push({
      name: "Lots",
      params: { stageId: route.params.stageId },
    });
  };

const __returned__ = { route, showTransitionModal, showReleaseModal, attentionType, showSendContractFormModal, toProfile, toast, estateId, stageId, lotId, stage, lot, eois, businesses, showCreateInitialHoldModal, userBusiness, initialHoldsCount, showCancelContractModal, showTransferOwnershipModal, estate, hasCollaborator, onCloseTransferOwnershipModal, canManageLot, getEstate, canTransferLot, transferOwnership, tabs, getLotStatus, currentTab, showEdit, showCreateOptionModal, createOptionObject, showConfirmActionModal, showConfirmMakeOffline, OfflinePhase, loading, onShowCancelContractModal, onCancelContract, makeOffline, onCreatedHold, changeOfflinePhase, toTab, changeTab, getLot, getEois, getBusinesses, getUserBusiness, getEoisWithWaitingForApprove, setInitialHoldsCount, checkForUnrelease, checkForCreateOption, checkProceedModal, closeCreateOptionModal, getCurrentTab, generateContract, closeReleaseModal, addDocument, deleteDocument, toStage, DropDown, SwitchComponent, EditLotModal, LotLifecycle, CreateOptionModal, LotDetailsConfirmModal, ReleaseLotsModal, TransitionModal, LotAssignmentModal, SendContractFormModal, CreateHoldFromLandownerSideModal, computed, onMounted, ref, watch, onBeforeUnmount, get router() { return router }, get store() { return store }, get getLotPreviousStatus() { return getLotPreviousStatus }, get getLotStatusClass() { return getLotStatusClass }, get formatDate() { return formatDate }, get useLotHook() { return useLotHook }, get StageInterface() { return StageInterface }, get LotStatus() { return LotStatus }, get LotDocument() { return LotDocument }, get LotInterface() { return LotInterface }, get EoiStatus() { return EoiStatus }, get EoiGroupByLotInterface() { return EoiGroupByLotInterface }, get useToast() { return useToast }, get LotOfflineUpdateInterface() { return LotOfflineUpdateInterface }, get OfflinePhases() { return OfflinePhases }, get useRoute() { return useRoute }, get InitialHoldWithLotInterface() { return InitialHoldWithLotInterface }, GiveReasonModal, get ReasonsForCancelEOI() { return ReasonsForCancelEOI }, TransferOwnershipModal, get EstateInterface() { return EstateInterface }, get getUser() { return getUser }, get isLotOwner() { return isLotOwner }, get AxiosResponse() { return AxiosResponse }, get Business() { return Business }, get getBusinessNameFromLot() { return getBusinessNameFromLot }, get getBusinessColorFromLot() { return getBusinessColorFromLot } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})